import { IconArrowRight } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { useUserGetWrong } from '../../api/hooks/User/useUserGetWrong'
import { useAuthStore } from '../../store/useAuthStore'
import { useWrongQuizStore } from '../../store/useWrongQuizStore'
import styles from '../../styles/user/LearningContainer.module.scss'
import BaseButton from '../common/BaseButton'
import FractionPreviewText from '../common/FractionPreviewText'
import Loading from '../common/Loading'

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '') || 'http://localhost:30022'
const toImgSrc = (url) => (!url ? '' : url.startsWith('http') ? url : `${API_ORIGIN}${url}`)

const LearningWrongQuizStep = ({ onQuizComplete, onExplanationUpdate }) => {
  const user = useAuthStore((state) => state.user)
  const {
    data: wrongData,
    isLoading,
    error,
  } = useUserGetWrong({
    ID: user?.ID || 0,
  })

  const {
    currentQuestionIndex,
    getCurrentQuestion,
    isLastQuestion,
    goToNextQuestion,
    submitAnswer,
    initializeWrongQuiz,
    userAnswers,
    setCurrentQuestionIndex,
    wrongQuestions,
  } = useWrongQuizStore()

  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [submitted, setSubmitted] = useState(null)
  const [isCompleting, setIsCompleting] = useState(false)

  // Math Notes state
  const [showMathNotes, setShowMathNotes] = useState(false)
  const [mathNoteTab, setMathNoteTab] = useState('note')
  const [noteText, setNoteText] = useState('')
  const [drawMode, setDrawMode] = useState('pen')
  const canvasRef = useRef(null)
  const isDrawingRef = useRef(false)
  const lastPosRef = useRef(null)

  useEffect(() => {
    if (user && wrongData?.data && wrongData.data.length > 0) {
      initializeWrongQuiz(user.ID, user.STEP, wrongData.data)
    }
  }, [user?.ID, user?.STEP, wrongData?.data])

  // 문제 변경 시 상태 초기화
  useEffect(() => {
    setSelectedAnswer(null)
    setSubmitted(null)
    setNoteText('')
    setDrawMode('pen')
    const canvas = canvasRef.current
    if (canvas) {
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
    }
  }, [currentQuestionIndex])

  // 문제 이동 시 이미 답한 문제면 해설 패널 복원, 아니면 닫기
  useEffect(() => {
    if (!currentQuestion) return
    const answered = userAnswers.find((a) => a.questionId === currentQuestion.QUESTION_ID)
    if (!answered) {
      onExplanationUpdate?.(null)
      return
    }
    const answerList = [
      { text: currentQuestion.ANSWER1, isCorrect: currentQuestion.IS_CORRECT_1 === 1 },
      { text: currentQuestion.ANSWER2, isCorrect: currentQuestion.IS_CORRECT_2 === 1 },
      { text: currentQuestion.ANSWER3, isCorrect: currentQuestion.IS_CORRECT_3 === 1 },
      { text: currentQuestion.ANSWER4, isCorrect: currentQuestion.IS_CORRECT_4 === 1 },
      { text: currentQuestion.ANSWER5, isCorrect: currentQuestion.IS_CORRECT_5 === 1 },
    ].filter((a) => a.text && a.text.trim() !== '')
    const corrIdx = answerList.findIndex((a) => a.isCorrect)
    onExplanationUpdate?.({
      isCorrect: answered.isCorrect,
      desc: currentQuestion.DESC,
      descUrl: currentQuestion.DESC_URL,
      correctAnswer: corrIdx + 1,
      correctAnswerIndex: corrIdx,
      selectedAnswerIndex: answered.selectedAnswerIndex,
    })
  }, [currentQuestionIndex, userAnswers.length])

  // Draw 탭 열릴 때 캔버스 픽셀 크기 초기화
  useEffect(() => {
    if (mathNoteTab !== 'draw' || !showMathNotes) return
    const timer = setTimeout(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const w = Math.round(rect.width)
      const h = Math.round(rect.height)
      if (w && h && (canvas.width !== w || canvas.height !== h)) {
        const tmp = document.createElement('canvas')
        tmp.width = canvas.width
        tmp.height = canvas.height
        tmp.getContext('2d').drawImage(canvas, 0, 0)
        canvas.width = w
        canvas.height = h
        canvas.getContext('2d').drawImage(tmp, 0, 0)
      }
    }, 50)
    return () => clearTimeout(timer)
  }, [mathNoteTab, showMathNotes])

  const currentQuestion = getCurrentQuestion()
  const isLast = isLastQuestion()

  if (isLoading) {
    return (
      <div className={styles.learningContainer}>
        <div className={styles.loadingWrapper}>
          <Loading />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.learningContainer}>
        <div className={styles.loadingWrapper}>
          <img src="/assets/rabbit-blue-monster-cry.png" alt="error" className={styles.errorImage} />
          <p className={styles.errorMessage}>Please try again later.</p>
          <button onClick={() => window.location.reload()} className={styles.retryButton}>Retry</button>
        </div>
      </div>
    )
  }

  if (!wrongData?.data || wrongData.data.length === 0) {
    return (
      <div className={styles.loadingWrapper}>
        <img src="/assets/confetti-monsters-2.png" alt="Congratulations" className={styles.errorImage} />
        <p className={styles.errorMessage}>
          <span className={styles.errorTitle}>Congratulations!</span>
          <br /> You have no incorrect answers.
        </p>
        <BaseButton className={styles.retryButton} onClick={onQuizComplete}>
          Next Step →
        </BaseButton>
      </div>
    )
  }

  if (!currentQuestion) return <div>Loading...</div>

  if (isCompleting) {
    return (
      <div className={styles.learningContainer}>
        <div className={styles.loadingWrapper}><Loading /></div>
      </div>
    )
  }

  const answers = [
    { text: currentQuestion.ANSWER1, isCorrect: currentQuestion.IS_CORRECT_1 === 1 },
    { text: currentQuestion.ANSWER2, isCorrect: currentQuestion.IS_CORRECT_2 === 1 },
    { text: currentQuestion.ANSWER3, isCorrect: currentQuestion.IS_CORRECT_3 === 1 },
    { text: currentQuestion.ANSWER4, isCorrect: currentQuestion.IS_CORRECT_4 === 1 },
    { text: currentQuestion.ANSWER5, isCorrect: currentQuestion.IS_CORRECT_5 === 1 },
  ].filter((a) => a.text && a.text.trim() !== '')

  const hasImageAnswers = answers.some((a) => String(a.text || '').startsWith('/'))
  const hasLongAnswers = answers.some(
    (a) => !String(a.text || '').startsWith('/') && String(a.text || '').length > 10
  )
  const hasVeryShortAnswers = !hasImageAnswers && answers.every((a) => String(a.text || '').length <= 8)
  const hasQuestionImage = !!(currentQuestion.IMG_URL || currentQuestion.IMG_URL2)
  const useSideBySide = hasQuestionImage && !hasImageAnswers

  // 기존 답변 (스토어)
  const storedAnswer = userAnswers.find((a) => a.questionId === currentQuestion.QUESTION_ID)
  const correctAnswerIndex = answers.findIndex((a) => a.isCorrect)

  const existingFeedback = storedAnswer
    ? {
        isCorrect: storedAnswer.isCorrect,
        selectedAnswerIndex: storedAnswer.selectedAnswerIndex,
        correctAnswerIndex,
        correctAnswer: correctAnswerIndex + 1,
      }
    : null

  // 활성 피드백: 이번 제출 > 기존 답변
  const activeFeedback = submitted ?? existingFeedback
  const isSubmitted = !!activeFeedback

  const handleSubmit = () => {
    if (selectedAnswer === null || !user) return

    const selectedAnswerData = answers[selectedAnswer]
    const corrIdx = answers.findIndex((a) => a.isCorrect)
    const correctAnswer = corrIdx !== -1 ? corrIdx + 1 : ''

    submitAnswer(currentQuestion.QUESTION_ID, selectedAnswer, selectedAnswerData.isCorrect)

    const feedbackData = {
      isCorrect: selectedAnswerData.isCorrect,
      selectedAnswerIndex: selectedAnswer,
      correctAnswerIndex: corrIdx,
      correctAnswer,
    }
    setSubmitted(feedbackData)

    onExplanationUpdate?.({
      isCorrect: selectedAnswerData.isCorrect,
      desc: currentQuestion.DESC,
      descUrl: currentQuestion.DESC_URL,
      correctAnswer,
      correctAnswerIndex: corrIdx,
      selectedAnswerIndex: selectedAnswer,
    })
  }

  const handleGoNext = async () => {
    const latestAnswers = useWrongQuizStore.getState().userAnswers

    const allAnswered = wrongQuestions.every((q) =>
      latestAnswers.some((a) => a.questionId === q.QUESTION_ID)
    )

    if (allAnswered) {
      setIsCompleting(true)
      await onQuizComplete()
      return
    }

    const nextUnansweredIndex = wrongQuestions.findIndex((q, idx) => {
      const answered = latestAnswers.some((a) => a.questionId === q.QUESTION_ID)
      return !answered && idx > currentQuestionIndex
    })

    if (nextUnansweredIndex !== -1) {
      setCurrentQuestionIndex(nextUnansweredIndex)
    } else if (!isLast) {
      goToNextQuestion()
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  // Canvas drawing
  const getCanvasPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect()
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top }
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const startDraw = (e) => {
    isDrawingRef.current = true
    lastPosRef.current = getCanvasPos(e, canvasRef.current)
  }

  const draw = (e) => {
    if (!isDrawingRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const pos = getCanvasPos(e, canvas)
    ctx.beginPath()
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.lineWidth = drawMode === 'eraser' ? 18 : 2.5
    ctx.strokeStyle = drawMode === 'eraser' ? '#FFFFFF' : '#1E3A5F'
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()
    lastPosRef.current = pos
  }

  const endDraw = () => {
    isDrawingRef.current = false
    lastPosRef.current = null
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
  }

  const getAnswerBtnClass = (index) => {
    const classes = [styles.answerCircleBtn]

    if (isSubmitted) {
      if (index === activeFeedback.selectedAnswerIndex && activeFeedback.isCorrect) {
        classes.push(styles.btnCorrect)
      } else if (index === activeFeedback.selectedAnswerIndex && !activeFeedback.isCorrect) {
        classes.push(styles.btnWrong)
      } else if (index === activeFeedback.correctAnswerIndex && !activeFeedback.isCorrect) {
        classes.push(styles.btnCorrectHighlight)
      }
    } else {
      if (index === selectedAnswer) classes.push(styles.btnSelected)
    }

    if (hasImageAnswers) classes.push(styles.imageItem)
    return classes.join(' ')
  }

  const getAnswerGridClass = () => {
    if (hasImageAnswers) return `${styles.answerOptionsGrid} ${styles.imageGrid}`
    if (useSideBySide) return styles.answerOptionsGrid
    if (hasLongAnswers) return styles.answerOptionsGrid
    if (hasVeryShortAnswers) return `${styles.answerOptionsGrid} ${styles.oneRow}`
    return `${styles.answerOptionsGrid} ${styles.twoCol}`
  }

  const answersGrid = (
    <div className={getAnswerGridClass()}>
      {answers.map((answer, index) => (
        <button
          key={index}
          className={getAnswerBtnClass(index)}
          onClick={() => !isSubmitted && setSelectedAnswer(index)}
          disabled={isSubmitted}
        >
          <span className={styles.answerNumCircle}>{index + 1}</span>
          <span className={styles.answerCircleText}>
            {String(answer.text).startsWith('/') ? (
              <img
                src={toImgSrc(answer.text)}
                alt={`Answer ${index + 1}`}
                className={styles.answerImgOption}
              />
            ) : (
              <FractionPreviewText text={answer.text} />
            )}
          </span>
        </button>
      ))}
    </div>
  )

  return (
    <div className={styles.quizStepInner}>
      {/* 문제 제목 */}
      <div className={styles.questionTitleCard}>
        <span className={styles.questionNum}>Q{currentQuestionIndex + 1}</span>
        <span><FractionPreviewText text={currentQuestion.TITLE} /></span>
      </div>

      {/* 이미지 + 보기 */}
      {useSideBySide ? (
        <div className={styles.imageAnswerLayout}>
          <div className={styles.imageAnswerLeft}>
            {currentQuestion.IMG_URL && (
              <img src={toImgSrc(currentQuestion.IMG_URL)} alt="DESC IMG" className={styles.questionImgSide} />
            )}
            {currentQuestion.IMG_URL2 && (
              <img src={toImgSrc(currentQuestion.IMG_URL2)} alt="DESC IMG" className={styles.questionImgSide} />
            )}
          </div>
          <div className={styles.imageAnswerRight}>{answersGrid}</div>
        </div>
      ) : (
        <>
          {hasQuestionImage && (
            <div className={styles.questionImagesSection}>
              {currentQuestion.IMG_URL && (
                <img src={toImgSrc(currentQuestion.IMG_URL)} alt="DESC IMG" className={styles.questionImgFull} />
              )}
              {currentQuestion.IMG_URL2 && (
                <img src={toImgSrc(currentQuestion.IMG_URL2)} alt="DESC IMG" className={styles.questionImgFull} />
              )}
            </div>
          )}
          {answersGrid}
        </>
      )}

      {/* 인라인 피드백 카드 */}
      {isSubmitted && (
        <div
          className={`${styles.feedbackCard} ${
            activeFeedback.isCorrect ? styles.feedbackCardCorrect : styles.feedbackCardWrong
          }`}
        >
          <div className={styles.feedbackCardLeft}>
            <span
              className={`${styles.feedbackCardIconCircle} ${
                activeFeedback.isCorrect ? styles.iconCorrect : styles.iconWrong
              }`}
            >
              {activeFeedback.isCorrect ? '✓' : '✗'}
            </span>
            <div>
              <div className={styles.feedbackCardTitle}>
                {activeFeedback.isCorrect ? 'Correct!' : 'Incorrect'}
              </div>
              <div className={styles.feedbackCardSub}>
                {activeFeedback.isCorrect ? 'Great job! Keep it up.' : 'Your answer is incorrect.'}
              </div>
            </div>
          </div>

          {!activeFeedback.isCorrect && activeFeedback.correctAnswer && (
            <div className={styles.feedbackCardRight}>
              <span className={styles.feedbackCardCorrectLabel}>Correct Answer</span>
              <div className={styles.feedbackCardCorrectRow}>
                <span className={styles.feedbackCardCorrectNum}>{activeFeedback.correctAnswer}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 하단 네비게이션 */}
      <div className={styles.quizNavigation}>
        <button
          className={styles.prevBtn}
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          ← Previous
        </button>

        {isSubmitted ? (
          <button className={styles.nextQuizBtn} onClick={handleGoNext}>
            Next <IconArrowRight size={16} />
          </button>
        ) : (
          <button
            className={styles.submitAnswerBtn}
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
          >
            Submit Answer
          </button>
        )}
      </div>

      {/* Math Notes */}
      <div className={styles.mathNotesSection}>
        <div
          className={styles.mathNotesToggleRow}
          onClick={() => setShowMathNotes((v) => !v)}
        >
          <span className={styles.mathNotesHint}>
            {showMathNotes ? <><img src="/assets/icons/note.png" alt="note" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Math Notes</> : <><img src="/assets/icons/hint.png" alt="hint" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Use this space to solve the problem.</>}
          </span>
          <button
            className={styles.mathNotesToggleBtn}
            onClick={(e) => { e.stopPropagation(); setShowMathNotes((v) => !v) }}
          >
            {showMathNotes ? 'Hide ▲' : 'Show Math Notes ▼'}
          </button>
        </div>

        {showMathNotes && (
          <div className={styles.mathNotesPanel}>
            <div className={styles.mathNotesPanelHeader}>
              <div className={styles.mathNotesTitleGroup}>
                <img src="/assets/icons/note.png" alt="note" className={styles.mathNotesTitleIcon} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                <span className={styles.mathNotesTitleText}>Math Notes</span>
                <span className={styles.mathNotesTitleSub}>Show your work here!</span>
              </div>
              <div className={styles.mathNotesTabs}>
                <button
                  className={`${styles.mathNotesTab} ${mathNoteTab === 'note' ? styles.mathNotesTabActive : ''}`}
                  onClick={() => setMathNoteTab('note')}
                >
                  <img src="/assets/icons/note.png" alt="note" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Note
                </button>
                <button
                  className={`${styles.mathNotesTab} ${mathNoteTab === 'draw' ? styles.mathNotesTabActive : ''}`}
                  onClick={() => setMathNoteTab('draw')}
                >
                  <img src="/assets/icons/pencil.png" alt="draw" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Draw
                </button>
              </div>
            </div>

            <textarea
              className={`${styles.mathNotesTextarea} ${mathNoteTab !== 'note' ? styles.mathNotesHidden : ''}`}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Write your thinking here..."
              maxLength={1000}
            />
            {mathNoteTab === 'note' && (
              <div className={styles.mathNotesCounter}>{noteText.length} / 1000</div>
            )}

            <div className={`${styles.mathNotesCanvasWrapper} ${mathNoteTab !== 'draw' ? styles.mathNotesHidden : ''}`}>
              <canvas
                ref={canvasRef}
                className={styles.mathNotesCanvas}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
              <div className={styles.mathNotesDrawTools}>
                <button
                  className={`${styles.mathNotesDrawBtn} ${drawMode === 'pen' ? styles.mathNotesDrawBtnActive : ''}`}
                  onClick={() => setDrawMode('pen')}
                >
                  <img src="/assets/icons/pencil.png" alt="pen" style={{ width: 14, height: 14, objectFit: 'contain', verticalAlign: 'middle', marginRight: 4 }} />Pen
                </button>
                <button
                  className={`${styles.mathNotesDrawBtn} ${drawMode === 'eraser' ? styles.mathNotesDrawBtnActive : ''}`}
                  onClick={() => setDrawMode('eraser')}
                >
                  🧹 Eraser
                </button>
                <button className={styles.mathNotesClearBtn} onClick={clearCanvas}>
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LearningWrongQuizStep
